import type { ReactNode } from "react";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

import { apiClient } from "@alaffia-technology-solutions/client-sdk-react-query";
import { toast } from "@alaffia-technology-solutions/ds";

import { UserTypeEnum } from "~/gql/graphql";

import { useUserContext } from "./User.context";

const CLAIM_TRANSITION_CAPABILITY = "claim.canTransitionStage";

export interface CapabilitiesContextValue {
  capabilities: string[] | undefined;
  canTransitionClaimStage: boolean | undefined;
  refreshCapabilities: () => void;
}

export const CapabilitiesContext =
  createContext<CapabilitiesContextValue | null>(null);
const unresolvedCapabilities: CapabilitiesContextValue = {
  capabilities: undefined,
  canTransitionClaimStage: undefined,
  refreshCapabilities: () => undefined,
};

export const CapabilitiesProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { userType } = useUserContext();
  const lastGoodCapabilities = useRef<string[] | undefined>(undefined);
  const { data, error, refetch } = apiClient.api.capabilities.useQuery({
    filter: [CLAIM_TRANSITION_CAPABILITY],
  });

  if (data) lastGoodCapabilities.current = data;

  const refreshCapabilities = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    refreshCapabilities();
  }, [location.key, refreshCapabilities]);

  useEffect(() => {
    const onFocus = () => refreshCapabilities();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshCapabilities]);

  useEffect(() => {
    if (!error || lastGoodCapabilities.current) return;
    toast.warning("Unable to refresh claim stage permissions");
  }, [error]);

  let capabilities = lastGoodCapabilities.current;
  if (
    error &&
    !capabilities &&
    [
      UserTypeEnum.ADMINISTRATOR,
      UserTypeEnum.AUDITOR,
      UserTypeEnum.SYS,
    ].includes(userType)
  ) {
    capabilities = [CLAIM_TRANSITION_CAPABILITY];
  }

  return (
    <CapabilitiesContext
      value={{
        capabilities,
        canTransitionClaimStage: capabilities?.includes(
          CLAIM_TRANSITION_CAPABILITY,
        ),
        refreshCapabilities,
      }}
    >
      {children}
    </CapabilitiesContext>
  );
};

export const useCapabilities = () => {
  const context = useContext(CapabilitiesContext);
  return context ?? unresolvedCapabilities;
};
